import React from "react";
import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  SxProps,
  Theme
} from "@mui/material";
import { Control, Controller, FieldPath, FieldValues } from "react-hook-form";
import { useKivunovaTranslation } from "@kivunova/kivufrontendcommon";
import {
  VAT_CALCULATION_MODE_OPTIONS,
  VAT_PERCENTAGE_OPTIONS
} from "./constants/companyVatConstants";

interface CompanyVatFieldsProps<T extends FieldValues> {
  control: Control<T>;
  vatPercentageName: FieldPath<T>;
  vatCalculationModeName: FieldPath<T>;
  selectSize?: "small" | "medium";
  selectSx?: SxProps<Theme>;
  vatPercentageTestId?: string;
  vatCalculationModeTestId?: string;
}

function CompanyVatFields<T extends FieldValues>({
  control,
  vatPercentageName,
  vatCalculationModeName,
  selectSize = "medium",
  selectSx,
  vatPercentageTestId = "company-vat-percentage",
  vatCalculationModeTestId = "company-vat-calculation-mode"
}: CompanyVatFieldsProps<T>) {
  const { t } = useKivunovaTranslation();

  return (
    <>
      <Controller
        name={vatPercentageName}
        control={control}
        render={({ field, fieldState }) => (
          <FormControl fullWidth size={selectSize} error={!!fieldState.error}>
            <InputLabel shrink id="vat-percentage-select-label">
              {t("vat-percentage-label", "VAT percentage")}
            </InputLabel>
            <Select
              {...field}
              labelId="vat-percentage-select-label"
              id="vat-percentage-select"
              label={t("vat-percentage-label", "VAT percentage")}
              value={field.value ?? ""}
              onChange={(e) => field.onChange(Number(e.target.value))}
              inputProps={{ "data-testid": vatPercentageTestId }}
              sx={selectSx}
              MenuProps={{ container: document.body }}
            >
              {VAT_PERCENTAGE_OPTIONS.map((pct) => (
                <MenuItem key={pct} value={pct} id={`vat-percentage-${pct}`}>
                  {t(`vat-percentage-${pct}`, `${pct}%`)}
                </MenuItem>
              ))}
            </Select>
            {fieldState.error?.message && (
              <FormHelperText>{fieldState.error.message}</FormHelperText>
            )}
          </FormControl>
        )}
      />

      <Controller
        name={vatCalculationModeName}
        control={control}
        render={({ field, fieldState }) => (
          <FormControl fullWidth size={selectSize} error={!!fieldState.error}>
            <InputLabel shrink id="vat-calculation-mode-select-label">
              {t("vat-calculation-mode-label", "VAT calculation mode")}
            </InputLabel>
            <Select
              {...field}
              labelId="vat-calculation-mode-select-label"
              id="vat-calculation-mode-select"
              label={t("vat-calculation-mode-label", "VAT calculation mode")}
              value={field.value ?? ""}
              inputProps={{ "data-testid": vatCalculationModeTestId }}
              sx={selectSx}
              MenuProps={{ container: document.body }}
            >
              {VAT_CALCULATION_MODE_OPTIONS.map(
                ({ value, labelKey, defaultLabel }) => (
                  <MenuItem
                    key={value}
                    value={value}
                    id={`vat-calculation-mode-${value}`}
                  >
                    {t(labelKey, defaultLabel)}
                  </MenuItem>
                )
              )}
            </Select>
            {fieldState.error?.message && (
              <FormHelperText>{fieldState.error.message}</FormHelperText>
            )}
          </FormControl>
        )}
      />
    </>
  );
}

export default CompanyVatFields;
